import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import Card from '@/components/Card';
import { Input } from '@/components/Controls/Input';
import { Datatable } from '@/components/Datatable/Datatable';
import { Select } from '@/components/Controls/Select';
import { useListAllDistributors, useListAllGroups } from '@/hooks/group.hooks';
import { useListAllCompanies } from '@/hooks/company.hooks';
import { TableViewProps } from '@/type/listing.types';
import ButtonAdvancedFilter from '@/components/Custom/Button/ButtonAdvancedFilter';
import StructureFilter from '@/components/Custom/Filter/StructureFilter';
import { mapFilterSelect } from '@/utils/map.utils';
import { getOptionsTypeFilter } from '@/utils/constant.utils';
import { useStructureFilter } from '@/hooks/filters/structure-filter.hooks';
import { TableTitle } from '@/components/Header/TableTitle';
import Loader from '@/components/Loading/Loader';
import { useParams } from 'react-router-dom';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import { RoleName } from '@/api/entities/user-role-type';
import { StructureType } from '@/entities/structure/common';
import { ClearButton } from '@/components/Custom/Button/ClearButton';

const limit = 20;

export const BranchTableView = <TSchema extends FieldValues>({
  t,
  useGetAllData,
  onGetTotalActive,
  tableHeaders: TableHeaders,
  tableData: TableData,
  pendingDeactivate,
  onDeactivate,
}: TableViewProps<TSchema>) => {
  const { groupId, companyId } = useParams();
  const { primaryRole, loggedInUser } = useAuth();
  const { isAdmin, isGroupManager, isCompanyManager } = useUserRole();
  const [totalPages, setTotalPages] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  /**
   * === Filter Section ===
   */
  const {
    debouncedFilter,
    setDebouncedFilter,
    filtersEmpty,
    clearFilters,
    key,
    type,
    searchByKey,
    searchByTypes,
    startDate,
    endDate,
    isWhiteMark,
    distributorId,
    groupId: groupIdStrogage,
    companyId: companyIdStorage,
    searchBeginDate,
    searchEndDate,
    searchWhiteMark,
    searchByDistributor,
    searchByGroup,
    searchByCompany,
  } = useStructureFilter({
    structureType: StructureType.BRANCH,
    setCurrentPage,
  });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      role: primaryRole !== RoleName.ADMIN ? primaryRole : undefined,
      userId: loggedInUser?.id,
      groupId,
      companyId,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryRole, loggedInUser?.id, groupId, companyId]);
  /**
   * === END OF Filter Section ===
   */

  /**
   * === Data Fetching section ===
   */
  const {
    data: allData,
    isLoading: isLoadingAllData,
    isPending: isPendingAllData,
  } = useGetAllData(
    debouncedFilter,
    currentPage,
    limit,
    sortColumn,
    sortDirection
  );

  const { data: allDistributors, isLoading: isLoadingAllDistributors } =
    useListAllDistributors();
  const { data: allGroups, isLoading: isLoadingAllGroups } = useListAllGroups({
    distributorId: debouncedFilter?.distributorId,
  });
  const { data: allCompanies, isLoading: isLoadingAllCompanies } =
    useListAllCompanies({
      distributorId: debouncedFilter?.distributorId,
      groupId: debouncedFilter?.groupId,
    });
  /**
   * === END OF Data Fetching section ===
   */

  /**
   * === Pagination Section ===
   */
  useEffect(() => {
    setTotalPages(allData?.data?.totalPages);
    onGetTotalActive?.(allData?.data?.['total']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData]);
  /**
   * === END OF Pagination Section ===
   */

  /**
   * === Sorting Section ===
   */
  const handleSort = (column: string) => {
    setSortColumn(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  /**
   * === END OF Sorting Section ===
   */

  return (
    <>
      <Card bodyClassName="flex flex-col gap-5">
        <div className="flex items-center gap-5">
          <div className="flex flex-1">
            <TableTitle text={t('common:branches')} />
          </div>
          <div className="flex gap-5">
            <Input
              placeholder={t('common:searchBranch')}
              type="text"
              name="search"
              autoComplete="off"
              leftIcon={
                <i className="ki-outline ki-magnifier text-lg text-gray-8990A8" />
              }
              inputWrapperClassName="w-[300px]"
              inputClassName="h-10"
              value={key}
              onChange={(e) => searchByKey(e.target.value)}
            />
            <Select
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500 max-w-40"
              options={getOptionsTypeFilter(t)}
              value={type}
              onChange={(e) => searchByTypes(e.target.value)}
            />
            <ButtonAdvancedFilter>
              <StructureFilter
                t={t}
                isLoadingAllDistributors={isLoadingAllDistributors}
                isLoadingAllGroups={isLoadingAllGroups}
                isLoadingAllCompanies={isLoadingAllCompanies}
                distributorOptions={mapFilterSelect(allDistributors?.data)}
                groupOptions={mapFilterSelect(allGroups?.data)}
                companyOptions={mapFilterSelect(allCompanies?.data)}
                displayDistributorFilter={isAdmin || isGroupManager}
                displayGroupFilter={isAdmin || isGroupManager}
                displayCompanyFilter={
                  isAdmin || isGroupManager || isCompanyManager
                }
                startDate={startDate}
                endDate={endDate}
                isWhiteMark={isWhiteMark}
                distributorId={distributorId}
                groupId={groupIdStrogage}
                companyId={companyIdStorage}
                searchBeginDate={searchBeginDate}
                searchEndDate={searchEndDate}
                searchWhiteMark={searchWhiteMark}
                searchByDistributor={searchByDistributor}
                searchByGroup={searchByGroup}
                searchByCompany={searchByCompany}
              />
            </ButtonAdvancedFilter>
            <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
          </div>
        </div>
        <div>
          <Datatable
            t={t}
            headers={<TableHeaders t={t} onSort={handleSort} />}
            data={
              isLoadingAllData ? (
                <></>
              ) : !allData?.data?.data?.length ? null : (
                <TableData
                  t={t}
                  data={allData?.data?.data}
                  pendingDeactivate={pendingDeactivate}
                  onDeactivate={onDeactivate}
                />
              )
            }
            showPagination={allData?.data?.data?.length > 0 && totalPages > 1}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </Card>
      {(isLoadingAllData || isPendingAllData || pendingDeactivate) && (
        <Loader />
      )}
    </>
  );
};
